import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Clock from 'lucide-react-native/icons/clock';
import ExternalLink from 'lucide-react-native/icons/external-link';
import MapPin from 'lucide-react-native/icons/map-pin';
import Star from 'lucide-react-native/icons/star';
import { ScrollView, StyleSheet, Text, View, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { RelatedList } from '@/components/detail/related-list';
import { TourHead } from '@/components/detail/tour-head';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, MaxContentWidth, Radius, Spacing } from '@/constants/theme';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useRelatedTours } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { useTour } from '@todo/core/hooks/use-tour';
import { cities } from '@/lib/events-data';
import { currencySymbol } from '@todo/core/i18n/format';
import { localizedTourDescription, localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { getPrefetched } from '@todo/core/static-prefetch';
import { isTourDelisted, type Tour } from '@todo/core/tours';

export { generateTourStaticParams as generateStaticParams } from '@todo/core/detail/tour-detail';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

export default function TourScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isLg } = useBreakpoint();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { tour: fetchedTour, loading, error } = useTour(id);

  // At build time (static export) the client fetch hasn't run; fall back to the
  // record generateStaticParams stashed so meta + content land in the HTML.
  const tour = fetchedTour ?? getPrefetched<Tour>('tour', id) ?? null;

  const cityName = tour?.city_id
    ? (cities.find((c) => c.id === tour.city_id)?.name ?? tour.city_id)
    : null;
  const title = tour ? localizedTourTitle(tour, locale) : '';
  const description = tour ? localizedTourDescription(tour, locale) : null;

  const { related, relatedSeedKey, seeAllHref, cityId, wine } = useRelatedTours(tour);
  const relatedHeading = wine
    ? cityId
      ? t('tours.moreWineInCity', { city: cityName })
      : t('tours.moreWine')
    : cityId
      ? t('tours.moreInCity', { city: cityName })
      : t('tours.more');

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      <Stack.Screen options={{ headerShown: false }} />
      {tour ? (
        <TourHead
          tour={tour}
          locale={locale}
          related={related}
          relatedSeedKey={relatedSeedKey}
        />
      ) : null}
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}>
        {tour ? (
          <>
          <View style={styles.centered}>
            <View style={[styles.page, isLg && styles.pageLg]}>
              <View style={[styles.heroBox, isLg && styles.heroBoxLg]}>
                {tour.image_url ? (
                  <Image source={{ uri: tour.image_url }} style={styles.hero} contentFit="cover" />
                ) : (
                  <View style={[styles.hero, { backgroundColor: theme.muted }]} />
                )}
                <LinearGradient
                  colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']}
                  style={styles.heroScrim}
                />
                <Pressable
                  accessibilityLabel={t('a11y.back')}
                  onPress={() => router.back()}
                  style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
                  <ArrowLeft size={20} color="#fff" />
                </Pressable>
                <View style={styles.heroBadge}>
                  <Badge variant="gold">{t('tours.badge')}</Badge>
                </View>
              </View>

              <View
                style={[
                  styles.card,
                  isLg && styles.cardLg,
                  { backgroundColor: theme.card, borderColor: theme.border },
                ]}>
                <Text
                  style={[
                    styles.title,
                    isLg && styles.titleLg,
                    { color: theme.text, fontFamily: Fonts?.serif },
                  ]}>
                  {title}
                </Text>

                <View style={styles.metaList}>
                  {tour.rating != null ? (
                    <View style={styles.metaRow}>
                      <Star size={16} color={theme.gold} fill={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>
                        {tour.rating.toFixed(1)}
                        {tour.review_count != null
                          ? ` · ${t('tours.reviews', { count: tour.review_count })}`
                          : ''}
                      </Text>
                    </View>
                  ) : null}
                  {tour.duration ? (
                    <View style={styles.metaRow}>
                      <Clock size={16} color={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>
                        {tour.duration}
                      </Text>
                    </View>
                  ) : null}
                  {cityName ? (
                    <View style={styles.metaRow}>
                      <MapPin size={16} color={theme.gold} />
                      <Text style={[styles.metaText, { color: theme.text }]}>{cityName}</Text>
                    </View>
                  ) : null}
                </View>

                {/* A delisted tour gets a notice instead of the price + Book
                    button — Viator no longer sells it (isTourDelisted). */}
                {isTourDelisted(tour) ? (
                  <View style={[styles.delistedNotice, { backgroundColor: theme.muted }]}>
                    <Text style={[styles.delistedBanner, { color: theme.text }]}>
                      {t('tours.delistedBanner')}
                    </Text>
                    <Text style={[styles.delistedHint, { color: theme.mutedForeground }]}>
                      {t('tours.delistedHint')}
                    </Text>
                  </View>
                ) : null}

                {!isTourDelisted(tour) && tour.from_price != null ? (
                  <View style={styles.priceRow}>
                    <Text style={[styles.price, { color: theme.gold }]}>
                      {formatPrice(tour.from_price, tour.currency, t)}
                    </Text>
                    <Text style={[styles.perPerson, { color: theme.mutedForeground }]}>
                      {t('tours.perPerson')}
                    </Text>
                  </View>
                ) : null}

                {!isTourDelisted(tour) && tour.booking_url ? (
                  <>
                    <Button
                      variant="gold"
                      fullWidth
                      style={styles.bookBtn}
                      leadingIcon={<ExternalLink size={18} color={theme.goldForeground} />}
                      onPress={() =>
                        openExternal(tour.booking_url as string, {
                          kind: 'tour',
                          source: tour.source,
                          id: tour.id,
                          title,
                          city: tour.city_id,
                          locale,
                        })
                      }>
                      {t('tours.book')}
                    </Button>
                    <Text style={[styles.via, { color: theme.mutedForeground }]}>
                      {t('tours.viaViator')}
                    </Text>
                  </>
                ) : null}

                {description ? (
                  <>
                    <View style={[styles.divider, { backgroundColor: theme.border }]} />
                    <Text
                      style={[
                        styles.sectionHeading,
                        { color: theme.text, fontFamily: Fonts?.serif },
                      ]}>
                      {t('tours.aboutExperience')}
                    </Text>
                    <Text style={[styles.description, { color: theme.textSecondary }]}>
                      {description}
                    </Text>
                  </>
                ) : null}
              </View>
            </View>
          </View>
          {related.length > 0 ? (
            <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
              {related.map((r) => (
                <TourCard key={r.id} tour={r} eager={false} />
              ))}
            </RelatedList>
          ) : null}
          </>
        ) : loading ? (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('tours.loadingOne')}
          </Text>
        ) : error ? (
          <Text style={[styles.statusText, { color: theme.destructive }]}>
            {t('tours.loadError', { error })}
          </Text>
        ) : (
          <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
            {t('tours.notFound')}
          </Text>
        )}
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
  },
  pageLg: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
  },
  heroBox: {
    width: '100%',
    aspectRatio: 16 / 9,
    position: 'relative',
  },
  heroBoxLg: {
    borderRadius: Radius['2xl'],
    overflow: 'hidden',
  },
  hero: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  card: {
    marginTop: -Spacing.four,
    borderTopLeftRadius: Radius['2xl'],
    borderTopRightRadius: Radius['2xl'],
    borderWidth: 1,
    borderBottomWidth: 0,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    paddingBottom: Spacing.five,
  },
  cardLg: {
    marginTop: -Spacing.five,
    borderRadius: Radius['2xl'],
    borderBottomWidth: 1,
    paddingHorizontal: Spacing.five,
    paddingVertical: Spacing.five,
    maxWidth: 760,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 33,
  },
  titleLg: {
    fontSize: 34,
    lineHeight: 41,
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  priceRow: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: Spacing.two,
  },
  price: {
    fontSize: 20,
    fontWeight: '700',
  },
  perPerson: {
    fontSize: 13,
  },
  delistedNotice: {
    marginTop: Spacing.three,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  delistedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  delistedHint: {
    fontSize: 13,
  },
  bookBtn: {
    marginTop: Spacing.three,
  },
  via: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: Spacing.two,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  sectionHeading: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
});
